import { Loader2 } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { FilterBar, type JobPostingOption } from '@/components/filters/FilterBar';
import { DetailDrawer } from '@/components/submissions/DetailDrawer';
import { KanbanBoard } from '@/components/submissions/KanbanBoard';
import { SubmissionsTable } from '@/components/submissions/SubmissionsTable';
import { useSubmissionFilters } from '@/hooks/useSubmissionFilters';
import { toast } from '@/lib/toast';
import { getJobPostings } from '@/services/jobPostingService';
import { getBoard } from '@/services/reviewService';
import { exportSubmissionsCsv, getSubmissions, SUBMISSION_STAGES } from '@/services/submissionService';

import type { BoardLanes } from '@/types/board.types';
import type { PaginationMeta, Submission } from '@/types/submission.types';

const ALL_ROLES = ['Armed Officer', 'Event Security', 'Mobile Patrol', 'Supervisor', 'Unarmed Officer'];
const ALL_SITES = [
  'Bronx',
  'Brooklyn',
  'Broward',
  'Manhattan',
  'Miami-Dade',
  'Orlando',
  'Palm Beach',
  'Queens',
  'Staten Island',
  'Tampa',
];

export function DashboardPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const filters = useSubmissionFilters();

  const [submissions, setSubmissions] = useState<Submission[]>([]);
  const [meta, setMeta] = useState<PaginationMeta | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isExporting, setIsExporting] = useState(false);
  const [jobPostingOptions, setJobPostingOptions] = useState<JobPostingOption[]>([]);
  const [boardLanes, setBoardLanes] = useState<BoardLanes | null>(null);
  const [boardTotal, setBoardTotal] = useState(0);
  const [isBoardLoading, setIsBoardLoading] = useState(false);
  const [boardError, setBoardError] = useState<string | null>(null);
  const [selectedCardId, setSelectedCardId] = useState<string | null>(null);

  const renderStage = useCallback((stage: string) => t(`submissions.stages.${stage}`), [t]);

  // Fetch job postings for filter dropdown
  useEffect(() => {
    getJobPostings({ limit: 100 })
      .then((data) => {
        setJobPostingOptions(
          data.items.map((jp) => ({
            id: jp.jobPostingId,
            label: `${jp.postingNumber} — ${jp.title}`,
          })),
        );
      })
      .catch(() => {
        // Silently fail — filter just won't show options
      });
  }, []);

  useEffect(() => {
    let isCancelled = false;
    setIsLoading(true);
    setError(null);

    getSubmissions({
      q: filters.debouncedSearch,
      roles: filters.roles.length > 0 ? filters.roles.join(',') : undefined,
      sites: filters.sites.length > 0 ? filters.sites.join(',') : undefined,
      stages: filters.stages.length > 0 ? filters.stages.join(',') : undefined,
      jobPostingId: filters.jobPostingId.length > 0 ? filters.jobPostingId : undefined,
      from: filters.dateFrom,
      to: filters.dateTo,
      sortBy: filters.sortBy,
      sortOrder: filters.sortBy ? filters.sortOrder : undefined,
      page: filters.page,
    })
      .then((data) => {
        if (!isCancelled) {
          setSubmissions(data.items);
          setMeta(data.meta);
          setIsLoading(false);
        }
      })
      .catch((err: unknown) => {
        if (!isCancelled) {
          setError(err instanceof Error ? err.message : t('submissions.errorLoading'));
          setIsLoading(false);
        }
      });

    return () => {
      isCancelled = true;
    };
  }, [
    filters.debouncedSearch,
    filters.roles,
    filters.sites,
    filters.stages,
    filters.jobPostingId,
    filters.dateFrom,
    filters.dateTo,
    filters.sortBy,
    filters.sortOrder,
    filters.page,
    t,
  ]);

  const loadBoard = useCallback(async () => {
    setIsBoardLoading(true);
    setBoardError(null);
    try {
      const data = await getBoard({
        q: filters.debouncedSearch ?? undefined,
        roles: filters.roles.length > 0 ? filters.roles.join(',') : undefined,
        sites: filters.sites.length > 0 ? filters.sites.join(',') : undefined,
        jobPostingId: filters.jobPostingId.length > 0 ? filters.jobPostingId : undefined,
        from: filters.dateFrom ?? undefined,
        to: filters.dateTo ?? undefined,
      });
      setBoardLanes(data.lanes);
      setBoardTotal(data.total);
    } catch (err: unknown) {
      setBoardError(err instanceof Error ? err.message : t('board.errorLoading'));
    } finally {
      setIsBoardLoading(false);
    }
  }, [
    filters.debouncedSearch,
    filters.roles,
    filters.sites,
    filters.jobPostingId,
    filters.dateFrom,
    filters.dateTo,
    t,
  ]);

  // Fetch board data when in kanban view
  useEffect(() => {
    if (filters.viewMode !== 'kanban') return;
    void loadBoard();
  }, [filters.viewMode, loadBoard]);

  const handleExport = useCallback(async () => {
    setIsExporting(true);
    try {
      const searchTerm = filters.debouncedSearch.length > 0 ? filters.debouncedSearch : undefined;
      const fromDate = filters.dateFrom.length > 0 ? filters.dateFrom : undefined;
      const toDate = filters.dateTo.length > 0 ? filters.dateTo : undefined;
      await exportSubmissionsCsv({
        q: searchTerm,
        roles: filters.roles.length > 0 ? filters.roles : undefined,
        sites: filters.sites.length > 0 ? filters.sites : undefined,
        stages: filters.stages.length > 0 ? filters.stages : undefined,
        from: fromDate,
        to: toDate,
      });
      toast.success(t('submissions.exportSuccess'));
    } catch {
      toast.error(t('submissions.exportError'));
    } finally {
      setIsExporting(false);
    }
  }, [filters.debouncedSearch, filters.roles, filters.sites, filters.stages, filters.dateFrom, filters.dateTo, t]);

  const handleCardClick = useCallback((id: string) => {
    setSelectedCardId(id);
  }, []);

  const handleCloseDrawer = useCallback(() => {
    setSelectedCardId(null);
  }, []);

  function handleRowClick(submission: Submission) {
    void navigate(`/submissions/${submission.id}`);
  }

  return (
    <div className='space-y-6'>
      <FilterBar
        searchInput={filters.searchInput}
        onSearchChange={filters.setSearchInput}
        roles={filters.roles}
        allRoles={ALL_ROLES}
        onRolesChange={filters.setRoles}
        sites={filters.sites}
        allSites={ALL_SITES}
        onSitesChange={filters.setSites}
        stages={filters.stages}
        allStages={SUBMISSION_STAGES}
        onStagesChange={filters.setStages}
        renderStage={renderStage}
        jobPostingId={filters.jobPostingId}
        jobPostingOptions={jobPostingOptions}
        onJobPostingChange={filters.setJobPostingId}
        dateFrom={filters.dateFrom}
        dateTo={filters.dateTo}
        onDateFromChange={filters.setDateFrom}
        onDateToChange={filters.setDateTo}
        activeFilterCount={filters.activeFilterCount}
        onClearAll={filters.clearAll}
        onExport={handleExport}
        isExporting={isExporting}
        viewMode={filters.viewMode}
        onViewModeChange={filters.setViewMode}
      />

      {error && filters.viewMode === 'table' ? (
        <div className='bg-red-50 border border-red-200 rounded-lg p-4 text-sm text-red-700'>{error}</div>
      ) : filters.viewMode === 'table' ? (
        <SubmissionsTable
          submissions={submissions}
          isLoading={isLoading}
          onRowClick={handleRowClick}
          sortBy={filters.sortBy}
          sortOrder={filters.sortOrder}
          onSort={filters.handleSort}
          meta={meta}
          onPageChange={filters.setPage}
        />
      ) : (
        <>
          {boardError && (
            <div className='bg-red-50 border border-red-200 rounded-lg p-4 text-sm text-red-700'>{boardError}</div>
          )}

          {isBoardLoading && !boardLanes && (
            <div className='flex items-center justify-center py-16'>
              <Loader2 size={32} className='animate-spin text-gray-400' />
            </div>
          )}

          {boardLanes && (
            <>
              <p className='text-sm text-gray-500'>{t('board.totalApplicants', { count: boardTotal })}</p>
              <KanbanBoard lanes={boardLanes} onCardClick={handleCardClick} />
            </>
          )}

          <DetailDrawer cardId={selectedCardId} onClose={handleCloseDrawer} onBoardRefresh={() => void loadBoard()} />
        </>
      )}
    </div>
  );
}
